'use client';
import Image from 'next/image';
import type { ToastOptions } from 'react-toastify';
import { cssTransition, toast } from 'react-toastify';

import type { TToast } from './types';
import { BurstToast } from './ui/BurstToast';
import {
	DemoGenerationToast,
	LevelToast,
	NeuronsToast,
	Toast,
} from './ui/Toast';

const BagelTransition = cssTransition({
	enter: 'bagel-enter',
	exit: 'bagel-exit',
	collapseDuration: 1000,
});

const BurstTransition = cssTransition({
	enter: 'burst-enter',
	exit: 'burst-exit',
	collapseDuration: 1000,
});

const baseConfig = {
	containerId: 'main',
	draggable: true,
};

const bagelConfig: ToastOptions = {
	icon: false,
	draggable: false,
	className: 'bagel',
	autoClose: 3000,
	closeButton: false,
	closeOnClick: true,
	transition: BagelTransition,
};

export const Toaster = {
	show: ({ title, text, titleKey, textKey, ...cfg }: TToast & ToastOptions) =>
		toast.success(<Toast {...{ title, text, titleKey, textKey }} />, {
			icon: false,
			...baseConfig,
			...cfg,
		}),

	showSuccess: ({
		title,
		text,
		titleKey,
		textKey,
		...cfg
	}: TToast & ToastOptions) =>
		toast.success(<Toast {...{ title, text, titleKey, textKey }} />, {
			icon: () => (
				<Image src="/assets/icons/tick.svg" alt="tick" width={24} height={24} />
			),
			...baseConfig,
			...cfg,
		}),

	showInfo: ({ title, text, titleKey, textKey }: TToast) =>
		toast.info(<Toast {...{ title, text, titleKey, textKey }} />, baseConfig),

	showWarn: ({ title, text, titleKey, textKey }: TToast) =>
		toast.warn(<Toast {...{ title, text, titleKey, textKey }} />, baseConfig),

	showError: ({ title, text, titleKey, textKey }: TToast & ToastOptions) =>
		toast.error(<Toast {...{ title, text, titleKey, textKey }} />, baseConfig),

	showWithUndo: ({
		title,
		text,
		titleKey,
		textKey,
		onClose,
		onUndo,
	}: TToast & {
		onClose: () => void;
	}) => {
		let isUndone = false;

		const handleUndo = () => {
			isUndone = true;
			toast.dismiss(toastId);
			onUndo?.();
		};

		const toastId = toast.error(
			<Toast {...{ title, text, titleKey, textKey }} onUndo={handleUndo} />,
			{
				...baseConfig,
				autoClose: 4000,
				closeButton: false,
				closeOnClick: true,
				hideProgressBar: false,
				onClose: () => {
					if (!isUndone) {
						onClose();
					}
				},
			}
		);
	},

	showLevel: ({ level }: { level: number }) =>
		toast.success(<LevelToast level={level} />, {
			...baseConfig,
			...bagelConfig,
		}),

	showNeurons: ({ amount }: { amount: string }) =>
		toast.success(<NeuronsToast amount={amount} />, {
			...baseConfig,
			...bagelConfig,
			delay: 300,
		}),

	showImageGeneration: ({ amount }: { amount: string }) =>
		toast.success(<DemoGenerationToast amount={amount} />, {
			...baseConfig,
			...bagelConfig,
			delay: 300,
			autoClose: 10000,
		}),

	showNeuronsBurst: ({ amount }: { amount: string }) =>
		toast.success(<BurstToast amount={amount} />, {
			...baseConfig,
			containerId: 'center-toast',
			icon: false,
			draggable: false,
			autoClose: 100,
			closeButton: false,
			closeOnClick: true,
			className: 'burst',
			position: 'top-center',
			transition: BurstTransition,
		}),
};
